import { useState } from 'react';
import axios from 'axios';
import { Check, Hash, Lock, Pencil, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { useExternalReference } from '../../hooks/useExternalReference';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LOT B — Affichage + saisie de la référence externe sur une opération.
 * Verrou staff : une fois saisie par Baobab, le client ne peut plus la modifier.
 */
export const ExternalRefField = ({ operation, isStaff = false, onUpdated, className = '' }) => {
  // Côté staff, la config à appliquer est celle de la société cliente de l'opération
  // (le staff n'a pas de société) — sinon le champ resterait invisible.
  const { config } = useExternalReference(isStaff ? operation?.companyName : undefined);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(operation?.externalRef || '');
  const [saving, setSaving] = useState(false);

  if (!config.enabled && !operation?.externalRef) return null;

  const label = operation?.externalRefLabel || config.label || 'Référence externe';
  const lockedByStaff = !isStaff && operation?.externalRefSetBy === 'staff';
  const lockedByConfig = !isStaff && !config.clientCanEdit;
  const canEdit = isStaff || (!lockedByStaff && !lockedByConfig);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/operations/${operation.id}/external-reference`, { value });
      const notes = [];
      if (res.data?.duplicateCount) {
        notes.push(`Attention : cette référence est déjà utilisée sur ${res.data.duplicateCount} autre(s) opération(s).`);
      }
      if (res.data?.invoiceRegenerated) {
        notes.push('La facture déjà émise a été régénérée avec cette référence (même numéro).');
      } else if (res.data?.afterInvoice) {
        notes.push("Référence ajoutée après facturation : elle ressort dans le relevé et les exports.");
      }
      toast.success(`${label} enregistré`, { description: notes.join(' ') || undefined });
      setEditing(false);
      onUpdated?.(res.data);
    } catch (e) {
      toast.error('Erreur', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex items-start gap-2 ${className}`} data-testid="external-ref-field">
      <Hash className="w-4 h-4 mt-1 text-slate-400" strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500">{label}</p>
        {editing ? (
          <div className="flex items-center gap-1.5 mt-1">
            <Input
              value={value}
              maxLength={50}
              autoFocus
              placeholder={config.helpText || label}
              onChange={(e) => setValue(e.target.value)}
              className="h-8 text-sm"
              data-testid="external-ref-input"
            />
            <Button size="sm" className="h-8 px-2 bg-[#1db584] hover:bg-[#16a075]" disabled={saving}
              onClick={save} data-testid="external-ref-save-inline-btn">
              <Check className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setEditing(false); setValue(operation?.externalRef || ''); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${operation?.externalRef ? 'text-slate-900' : 'text-amber-600'}`}
              data-testid="external-ref-value">
              {operation?.externalRef || (config.required ? 'Non renseigné — requis' : 'Non renseigné')}
            </span>
            {canEdit ? (
              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-400 hover:text-slate-700"
                onClick={() => setEditing(true)} data-testid="external-ref-edit-btn">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400" title="Référence validée par Baobab">
                <Lock className="w-3 h-3" /> validée par Baobab
              </span>
            )}
          </div>
        )}
        {!editing && config.helpText && !operation?.externalRef && (
          <p className="text-[10px] text-slate-400 mt-0.5">{config.helpText}</p>
        )}
      </div>
    </div>
  );
};

export default ExternalRefField;
